import React, { useEffect, useCallback } from 'react';
import { Button, PageShell, VerificationBlock, WhiteBlock } from 'components';
import StackedColumnChart from 'components/StackedColumnChart';
import { useNavigate } from 'react-router-dom';
import { useProfile, useFilemakerSyncStatus } from 'hooks';
import { greeting } from 'helpers';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState, fetchInvoices } from 'data';
import { useTranslation } from 'react-i18next';
import { Address, BankingInfo, PersonalInfo } from 'data/slices/profileSlice';
import { profileCompletedApi } from 'data/api/userApi';
import Swal from 'sweetalert2';

interface UserProfile {
  id?: number;
  email?: string;
  role?: string;
  firstName: string;
  lastName: string;
  middleName: string;
  language: string;
  addresses: Address[];
  bankingInfo: BankingInfo[];
  personalInfo: PersonalInfo | null;
  identityCardAccepted?: boolean;
}

export const Dashboard = () => {
  	const { t } = useTranslation();
	const user = useProfile() as UserProfile;
	const navigate = useNavigate();
	const dispatch = useDispatch<AppDispatch>();
	const { invoices, count, getInvoicesLoading } = useSelector(
		(state: RootState) => state.invoices,
	);
  const [profileCompleted, setProfileCompleted] = React.useState(false);
  const [uploadedIdDoc, setUploadedIdDoc] = React.useState(false);
  const [contractConfirmed, setContractConfirmed] = React.useState(false);
  const [isLoadingVerification, setIsLoadingVerification] = React.useState(true);
  const { showBanner: showProfileProcessing } = useFilemakerSyncStatus();

  const showWelcomeAlert = useCallback(() => {
		Swal.fire({
			title: String(t('dashboard.welcome.title')),
			html: `
				<div style="text-align: left; font-family: system-ui, -apple-system, sans-serif;">
					<p style="margin-bottom: 16px; font-size: 16px; color: #666;">${String(t('dashboard.welcome.subtitle'))}</p>
					<ol style="margin: 16px 0; padding-left: 20px; line-height: 1.6;">
						<li style="margin-bottom: 8px;">1. ${String(t('dashboard.welcome.step1'))}</li>
						<li style="margin-bottom: 8px;">2. ${String(t('dashboard.welcome.step2'))}</li>
						<li style="margin-bottom: 8px;">3. ${String(t('dashboard.welcome.step3'))}</li>
					</ol>
					<p style="margin-top: 20px; font-size: 14px; color: #666;">${String(t('dashboard.welcome.needHelp'))}</p>
					<p style="margin-top: 8px; font-size: 14px; color: #0066cc;">📧 ${String(t('dashboard.welcome.email'))}</p>
				</div>
			`,
			icon: 'info',
			confirmButtonText: 'Got it!',
			width: 500,
			customClass: {
				popup: 'welcome-alert',
				confirmButton: 'swal-confirm-btn'
			}
		});
	}, [t]);

  useEffect(() => {
    const fetchData = async () => {
      dispatch(fetchInvoices({ page: 0, per_page: 10000 }));

      try {
        const profileCompletedResp = await profileCompletedApi();
        setProfileCompleted(profileCompletedResp.status);
        setUploadedIdDoc(profileCompletedResp.uploadedIdDoc);
        setContractConfirmed(profileCompletedResp.contractConfirmed);
        setIsLoadingVerification(false);

        // Show welcome alert for new users or those with incomplete profiles
        const hasSeenWelcome = localStorage.getItem('payflow_freelancer_welcome_shown');
        if (!hasSeenWelcome) {
          setTimeout(() => {
            showWelcomeAlert();
            localStorage.setItem('payflow_freelancer_welcome_shown', 'true');
          }, 500);
        }
      } catch (error) {
        console.error('Error fetching profile completed status:', error);
        setIsLoadingVerification(false);
      }
    };

    fetchData();
  }, [dispatch, showWelcomeAlert]);

	const totalInvoiceAmount = invoices.reduce(
		(sum, invoice) => sum + (invoice.amountInclVAT || 0),
		0,
	);

	const totalPayslipAmount = invoices.reduce(
    (sum, invoice) => sum + (Number(invoice.payslipAmount) || 0),
		0,
	);

	const currentYear = new Date().getFullYear();

	const chartData = invoices.map((invoice) => ({
		date: invoice.createdAt,
		amount: invoice.grossAmount || 0,
		status: invoice.status,
	}));

  return (
    <PageShell
      title={
        <>
          {t(greeting())}{' '}
          <span className="font-semibold">{user?.firstName}</span>
        </>
      }
      actions={
        <>
          <Button
            onClick={() => navigate('/salary-calculator')}
            label={t('salaryCalc.title')}
            color="#2E435F"
            fullWidth
          />
          <Button
            onClick={() => navigate('/invoices/create-new')}
            label={t('dashboard.createInvoice')}
            fullWidth
          />
        </>
      }
    >
        {!isLoadingVerification && (!profileCompleted || !uploadedIdDoc || !contractConfirmed || showProfileProcessing) ? (
          <div className="flex w-[100%]">
            <VerificationBlock
				profileStatus={profileCompleted}
				uploadedIdDoc={uploadedIdDoc}
				contractConfirmed={contractConfirmed}
				showProfileProcessing={showProfileProcessing}
            />
          </div>
        ) : null}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-[12px] md:gap-[24px]">
          <WhiteBlock
            onClickTooltip={() => {}}
            title={t('dashboard.totalInvoices')}
            amount={count}
            subtitle={t('dashboard.invoicesIssued')}
            loading={getInvoicesLoading}
          />
          <WhiteBlock
            onClickTooltip={() => {}}
            title={t('dashboard.totalInvoiceAmount')}
            amount={totalInvoiceAmount.toFixed(2)}
            subtitle={t('dashboard.amount')}
            currency="CHF"
            loading={getInvoicesLoading}
            formatted={true}
          />
          <WhiteBlock
            onClickTooltip={() => {}}
            title={t('dashboard.totalPayslipAmount')}
            amount={totalPayslipAmount.toFixed(2)}
            subtitle={t('dashboard.amount')}
            currency=""
            loading={getInvoicesLoading}
            formatted={true}
          />
        </div>
        <section className="bg-white p-[16px] md:p-[24px] flex flex-col border-[1px] border-[#E0E1E2] rounded-[16px] md:rounded-[24px] w-full">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-[12px] w-[100%] pb-[16px] md:pb-[24px]">
            <h2 className="font-medium text-[17px] md:text-[20px] text-[#2E435F]">
              {t('dashboard.invoicesIssuedInYearLabel', { year: currentYear })}
            </h2>
            <div className="flex items-center gap-[16px] text-[14px] text-[#4F5057]">
              <span className="flex items-center gap-[8px]">
                <span className="w-[14px] h-[14px] md:w-[20px] md:h-[20px] bg-[#D9EEFA] rounded-full" />
                {t('dashboard.open')}
              </span>
              <span className="flex items-center gap-[8px]">
                <span className="w-[14px] h-[14px] md:w-[20px] md:h-[20px] bg-[#00C2FF] rounded-full" />
                {t('dashboard.paid')}
              </span>
            </div>
          </div>
          <StackedColumnChart data={chartData} year={currentYear} />
        </section>
    </PageShell>
  );
};
